import { useQuery } from '@tanstack/react-query';
import type { BankAccountResponse, BankMovementsResponse } from '@k-management/shared';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { apiRequest } from '../../lib/api.js';
import { movementTitle, ReconcileForm, reconcileWindowPath } from './ReconcilePanel.js';
import { useTreasuryChannel } from './treasury-ui.js';

/**
 * The reconciliation in a window of its own (07/10/2026), to keep on another
 * screen while the main window is used for the rest of K-Management. The
 * movements page sends here the movement opened next; what is saved here
 * reloads the other windows.
 */
export function ReconcileWindowPage() {
  const { code = '', movementId = '' } = useParams();
  const navigate = useNavigate();
  useTreasuryChannel((message) => {
    if (message.type === 'open')
      void navigate(reconcileWindowPath(message.code, message.movementId), { replace: true });
  });
  const banks = useQuery({
    queryKey: ['treasury', 'banks'],
    queryFn: () => apiRequest<BankAccountResponse[]>('/treasury/banks'),
  });
  const data = useQuery({
    queryKey: ['treasury', 'banks', code, 'movements'],
    queryFn: () => apiRequest<BankMovementsResponse>(`/treasury/banks/${code}/movements`),
  });
  const account = banks.data?.find((item) => item.code === code);
  const movement = data.data?.movements.find((item) => item.id === movementId);
  useEffect(() => {
    document.title = movement
      ? `Riconciliazione · ${account?.alias ?? code} · ${movementTitle(movement)}`
      : 'Riconciliazione';
  }, [movement, account, code]);

  return (
    <main className="reconcile-window stack">
      <header className="reconcile-window-head">
        <div>
          <p className="muted small">Riconciliazione · {account?.alias ?? code}</p>
          <h1>{movement ? movementTitle(movement) : 'Movimento'}</h1>
        </div>
        <button type="button" onClick={() => window.close()}>
          Chiudi
        </button>
      </header>
      {data.isPending && <p className="muted">Caricamento…</p>}
      {data.error && <p className="error">{data.error.message}</p>}
      {data.data && !movement && <p className="error">Movimento non trovato.</p>}
      {movement && <ReconcileForm key={movement.id} code={code} movement={movement} />}
    </main>
  );
}
